// ─── ShopComparison ───────────────────────────────────────────────────────────

interface FlatItem {
  id:      string
  item_id: string
  name:    string
  emoji:   string
}

interface Props {
  listItems:    FlatItem[]
  purchasedIds: string[]
  onContinue:   () => void
  onSkip:       () => void
}

export default function ShopComparison({ listItems, purchasedIds, onContinue, onSkip }: Props) {
  const plannedCount    = listItems.length
  const boughtItems     = listItems.filter(item => purchasedIds.includes(item.item_id))
  const forgottenItems  = listItems.filter(item => !purchasedIds.includes(item.item_id))
  const boughtCount     = boughtItems.length
  const forgottenCount  = forgottenItems.length
  const completionRate  = plannedCount > 0
    ? Math.round((boughtCount / plannedCount) * 100)
    : 100

  const headerEmoji = completionRate === 100 ? '🎉'
    : completionRate >= 80 ? '👍'
    : completionRate >= 60 ? '😅'
    : '🛒'

  const circumference = 2 * Math.PI * 50

  return (
    <div className="fixed inset-0 z-50 bg-[#FAF8F4] flex flex-col max-w-[480px] mx-auto">

      <div className="flex-1 overflow-y-auto px-5 py-6">

        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-4xl mb-3">{headerEmoji}</p>
          <h2 className="text-2xl font-bold text-[#2C2A26] mb-1">Shop complete</h2>
          <p className="text-sm text-[#7A7468]">Here's how this trip went</p>
        </div>

        {/* Completion circle */}
        <div className="flex justify-center mb-8">
          <div className="relative w-32 h-32">
            <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#E8E2DC" strokeWidth="10" />
              <circle
                cx="60" cy="60" r="50"
                fill="none"
                stroke="#6B9E78"
                strokeWidth="10"
                strokeDasharray={`${circumference}`}
                strokeDashoffset={`${circumference * (1 - completionRate / 100)}`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-3xl font-bold text-[#2C2A26]">{completionRate}%</p>
              <p className="text-xs text-[#7A7468]">complete</p>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex gap-3 mb-6">
          <div className="flex-1 bg-[#E8F2EA] rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-[#6B9E78]">{boughtCount}</p>
            <p className="text-xs text-[#6B9E78] mt-0.5">items bought</p>
          </div>
          {forgottenCount > 0 && (
            <div className="flex-1 bg-[#FAEAE8] rounded-2xl p-4 text-center">
              <p className="text-2xl font-bold text-[#C25B4E]">{forgottenCount}</p>
              <p className="text-xs text-[#C25B4E] mt-0.5">forgot</p>
            </div>
          )}
        </div>

        {/* Forgotten items */}
        {forgottenItems.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-semibold text-[#9A9491] uppercase tracking-wide mb-3">
              Didn't get
            </p>
            <div className="space-y-2">
              {forgottenItems.map(item => (
                <div key={item.id}
                  className="flex items-center gap-3 bg-white rounded-xl border border-[#E8E2DC] p-3">
                  <span className="text-xl">{item.emoji}</span>
                  <p className="text-sm text-[#2C2A26] flex-1">{item.name}</p>
                  <span className="text-xs text-[#C25B4E] font-medium">Still needed</span>
                </div>
              ))}
            </div>
            <div className="mt-3 bg-[#FAF8F4] rounded-xl p-3">
              <p className="text-xs text-[#7A7468]">
                💡 These items will stay on your list for next time
              </p>
            </div>
          </div>
        )}

        {/* Bought items */}
        {boughtItems.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[#9A9491] uppercase tracking-wide mb-3">
              Got these
            </p>
            <div className="flex flex-wrap gap-2">
              {boughtItems.map(item => (
                <div key={item.id}
                  className="flex items-center gap-1.5 bg-[#E8F2EA] rounded-full px-3 py-1.5">
                  <span className="text-sm">{item.emoji}</span>
                  <span className="text-xs font-medium text-[#6B9E78]">{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Bottom buttons */}
      <div className="px-5 pb-8 pt-4 border-t border-[#F0EDE8] space-y-2">
        <button
          onClick={onContinue}
          className="w-full py-4 rounded-2xl text-base font-semibold text-white"
          style={{ background: '#6B9E78' }}
        >
          Continue to unpack →
        </button>
        <button
          onClick={onSkip}
          className="w-full py-2 text-sm text-[#9A9491]"
        >
          Skip
        </button>
      </div>

    </div>
  )
}
